import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import api, { BACKEND_URL } from "@/lib/api";
import { Video, Image as ImageIcon, Mic, Download, ShieldCheck, FolderOpen } from "lucide-react";

const KIND_FILTERS = [
  { id: "all",   label: "All" },
  { id: "video", label: "Video" },
  { id: "image", label: "Image" },
  { id: "audio", label: "Voice" },
];

export default function OwnerRendersPage() {
  const { user, loading } = useAuth();
  const [renders, setRenders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (user?.role !== "owner") return;
    setFetching(true);
    api.get("/admin/studio/renders")
      .then(({ data }) => setRenders(data.renders || []))
      .catch(() => setRenders([]))
      .finally(() => setFetching(false));
  }, [user]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/owner/renders" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;

  const filtered = filter === "all" ? renders : renders.filter(r => r.kind === filter);

  return (
    <AdminShell
      title={{ eyebrow: "Admin Library · Private", heading: "Admin Render History" }}
      subtitle="Every render produced from the Owner Studio. Completely separated from the customer-facing My Renders library. Files keep the discreet YBMStudios.com signature."
      breadcrumbs={[{ label: "Owner", to: "/owner" }, { label: "Renders" }]}
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex gap-2" data-testid="admin-renders-filter">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`admin-renders-filter-${f.id}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                filter === f.id
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-[#A0A0A0] uppercase tracking-wider flex items-center gap-2">
          <ShieldCheck className="w-3 h-3 text-[#D4AF37]" /> {filtered.length} admin render{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      {fetching ? (
        <div className="border border-[#2A2A2A] bg-[#0F0F0F] py-16 text-center text-[#A0A0A0] text-sm" data-testid="admin-renders-loading">
          Loading admin library…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-[#D4AF37]/30 bg-[#0F0F0F] py-16 text-center" data-testid="admin-renders-empty">
          <FolderOpen className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.4} />
          <p className="text-[#F4F4F0] font-semibold mb-2">No admin renders yet</p>
          <p className="text-[#A0A0A0] text-sm mb-6">Head to the studio and run your first private render.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/owner/studio/video" className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors">New Admin Video</Link>
            <Link to="/owner/studio/image" className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors">New Admin Image</Link>
            <Link to="/owner/studio/voiceover" className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors">New Admin Voice</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-renders-grid">
          {filtered.map((r) => <Card key={r.render_id} r={r} />)}
        </div>
      )}
    </AdminShell>
  );
}

function Card({ r }) {
  const Icon = r.kind === "video" ? Video : r.kind === "audio" ? Mic : ImageIcon;
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] hover:border-[#D4AF37]/50 transition-colors" data-testid={`admin-render-${r.render_id}`}>
      <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
        {r.kind === "image" ? (
          <img src={`${BACKEND_URL}${r.preview_url}`} alt={r.prompt} loading="lazy" className="w-full h-full object-cover" />
        ) : r.kind === "video" ? (
          <video src={`${BACKEND_URL}${r.preview_url}`} controls playsInline className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-3">
            <Mic className="w-10 h-10 text-[#D4AF37]" />
            <audio src={`${BACKEND_URL}${r.preview_url}`} controls className="w-full max-w-xs" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]">
            <Icon className="w-3 h-3" /> Admin · {r.kind}
          </span>
          <span className="text-[10px] text-[#5A5A5A]">{(r.created_at || "").slice(0, 10)}</span>
        </div>
        <p className="text-xs text-[#F4F4F0] line-clamp-2 mb-4" title={r.prompt}>{r.prompt}</p>
        <a
          href={`${BACKEND_URL}${r.download_url}`}
          data-testid={`admin-render-download-${r.render_id}`}
          className="block w-full text-center bg-[#D4AF37] text-[#080808] font-bold text-xs py-2.5 hover:bg-[#F0C84B] transition-colors"
        >
          <Download className="w-3 h-3 inline mr-1.5" /> Download (Admin)
        </a>
      </div>
    </div>
  );
}
